import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useAuth } from '@/core/AuthContext';
import { useTenantStore, useTenantChange, useUserTenants, useAllTenants } from '@/core/hooks/useTenant';
import { useFactoryStore, useFactoryChange } from '@/core/hooks/useFactory';
import { useUserFactories } from '@/core/hooks/useUserFactories';
import { useUserMode } from '@/core/hooks/useUserMode';
import type { AppPermission } from '@/core/types/permissions';

type FactoryRole = 'supervisor' | 'worker';

interface AppScopeValue {
  selectedTenantId: string | null;
  selectedFactoryId: string | null;
  hasTenantAccess: boolean;
  hasFactoryAccess: boolean;
  isSupervisor: boolean;
  role: FactoryRole | null;
  modePreference: 'operator' | 'supervisor';
  effectiveMode: 'operator' | 'supervisor';
  setModePreference: (mode: 'operator' | 'supervisor') => void;
  toggleModePreference: () => void;
  hasPermission: (permission: AppPermission) => boolean;
  scopeReady: boolean;
  isBootstrapping: boolean;
}

const AppScopeContext = createContext<AppScopeValue | undefined>(undefined);

export function AppScopeProvider({ children }: { children: ReactNode }) {
  const { profile, loading: authLoading, profileLoading } = useAuth();
  const { selectedTenantId, hasHydrated: tenantHydrated } = useTenantStore();
  const { selectedFactoryId, hasHydrated: factoryHydrated } = useFactoryStore();
  const { changeTenant } = useTenantChange();
  const { changeFactory } = useFactoryChange();
  const { modePreference, setModePreference, toggleModePreference } = useUserMode();

  const { data: memberships = [], isLoading: membershipsLoading } = useUserTenants();
  const { data: allTenants = [], isLoading: allTenantsLoading } = useAllTenants(!!profile?.is_super_admin);
  const { data: userFactories = [], isLoading: factoriesLoading } = useUserFactories();

  const approvedMemberships = useMemo(
    () => memberships.filter((membership) => membership.status === 'approved'),
    [memberships]
  );

  const tenantIds = useMemo(() => {
    if (profile?.is_super_admin) {
      return allTenants.map((tenant) => tenant.id);
    }
    return approvedMemberships.map((membership) => membership.tenant?.id ?? membership.tenant_id);
  }, [profile?.is_super_admin, allTenants, approvedMemberships]);

  const tenantDataReady =
    tenantHydrated &&
    !authLoading &&
    !profileLoading &&
    (profile?.is_super_admin ? !allTenantsLoading : !membershipsLoading);

  useEffect(() => {
    // Bootstrap tenant selection once auth, profile, and memberships are ready.
    if (!tenantDataReady) return;
    if (tenantIds.length === 0) {
      if (selectedTenantId) changeTenant(null);
      return;
    }
    if (selectedTenantId && tenantIds.includes(selectedTenantId)) return;
    changeTenant(tenantIds[0]);
  }, [tenantDataReady, tenantIds, selectedTenantId, changeTenant]);

  const approvedFactories = useMemo(
    () => userFactories.filter((factory) => factory.status === 'approved'),
    [userFactories]
  );
  const factoryIds = useMemo(
    () => approvedFactories.map((factory) => factory.factory_id),
    [approvedFactories]
  );

  useEffect(() => {
    // Keep factory selection valid within the selected tenant.
    if (!factoryHydrated) return;
    if (!selectedTenantId) {
      if (selectedFactoryId) changeFactory(null);
      return;
    }
    if (factoriesLoading) return;
    if (factoryIds.length === 0) {
      if (selectedFactoryId) changeFactory(null);
      return;
    }
    if (selectedFactoryId && factoryIds.includes(selectedFactoryId)) return;
    changeFactory(factoryIds[0]);
  }, [
    factoryHydrated,
    selectedTenantId,
    factoriesLoading,
    factoryIds,
    selectedFactoryId,
    changeFactory,
  ]);

  const tenantBootstrapComplete =
    tenantDataReady && (tenantIds.length === 0 || (!!selectedTenantId && tenantIds.includes(selectedTenantId)));
  const factoryBootstrapComplete = !selectedTenantId
    ? true
    : factoryHydrated && !factoriesLoading && (factoryIds.length === 0 || (!!selectedFactoryId && factoryIds.includes(selectedFactoryId)));
  const scopeReady = tenantBootstrapComplete && factoryBootstrapComplete;

  const currentRole = approvedFactories.find((factory) => factory.factory_id === selectedFactoryId)?.role ?? null;
  const role: FactoryRole | null = profile?.is_super_admin ? 'supervisor' : currentRole;
  const isSupervisor = role === 'supervisor';

  const effectiveMode = isSupervisor ? modePreference : 'operator';

  const hasPermission = (_permission: AppPermission) => {
    if (profile?.is_super_admin) return true;
    return isSupervisor;
  };

  const hasTenantAccess = profile?.is_super_admin ? true : approvedMemberships.length > 0;
  const hasFactoryAccess = approvedFactories.length > 0;
  const isBootstrapping = !scopeReady;

  const value: AppScopeValue = {
    selectedTenantId,
    selectedFactoryId,
    hasTenantAccess,
    hasFactoryAccess,
    isSupervisor,
    role,
    modePreference,
    effectiveMode,
    setModePreference,
    toggleModePreference,
    hasPermission,
    scopeReady,
    isBootstrapping,
  };

  return <AppScopeContext.Provider value={value}>{children}</AppScopeContext.Provider>;
}

export const useAppScope = () => {
  const context = useContext(AppScopeContext);
  if (!context) {
    throw new Error('useAppScope must be used within AppScopeProvider');
  }
  return context;
};
